import React, { useCallback, useEffect, useRef, useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import { Modal } from '@/components/ui/Modal'
import {
  getEmails,
  getEmailTemplates,
  getFromAddresses,
  getMergeFields,
  composeEmail,
  sendEmail,
  FromAddressOption,
  MergeFieldOption,
} from '@/api/tapEmails'
import { TapEmailMessage, TapEmailTemplate } from '@/types/index'

// Feature #162 — Emails tab for the Application detail page.
// Lists sent (outbound) + received (inbound) emails and lets MANAGE_TAP users
// compose + send a new outbound email, optionally starting from a template.
// Templates expose merge fields (applicant_first_name, position_title, …)
// which the backend interpolates on `compose_email` so the modal shows a
// live preview before sending.
interface EmailsTabProps {
  applicationId: string
  // Used as the default `to` on the compose form when the applicant has an
  // email on record. Kept optional so the tab still renders for legacy
  // applications without an applicant.
  defaultToAddress?: string
  // Feature #238: parent can request the Compose modal opens automatically
  // (e.g. after transitioning to Outcome=Unsuccessful). The signal is
  // edge-triggered — parent flips it true and we acknowledge via
  // ``onAutoOpenHandled`` so the Compose modal can be reopened on a
  // subsequent transition.
  autoOpenCompose?: boolean
  onAutoOpenHandled?: () => void
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

// Feature #170: toolbar button style for the rich text editor.
const toolbarBtnStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: '12px',
  fontFamily: 'var(--font-family)',
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '4px',
  cursor: 'pointer',
  color: 'var(--color-black)',
  lineHeight: '1.4',
}

const directionBadgeStyle = (direction?: string): React.CSSProperties => {
  const isOutbound = (direction || '').toLowerCase() === 'outbound'
  return {
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: isOutbound ? '#fdf2f8' : '#f0f9ff',
    color: isOutbound ? 'var(--color-magenta)' : '#075985',
    textTransform: 'capitalize',
  }
}

const EmailsTab: React.FC<EmailsTabProps> = ({ applicationId, defaultToAddress, autoOpenCompose, onAutoOpenHandled }) => {
  const [emails, setEmails] = useState<TapEmailMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Compose modal state
  const [composeOpen, setComposeOpen] = useState(false)
  const [templates, setTemplates] = useState<TapEmailTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('')
  const [fromAddress, setFromAddress] = useState('')
  const [fromAddresses, setFromAddresses] = useState<FromAddressOption[]>([])
  const [toAddresses, setToAddresses] = useState('')
  const [ccAddresses, setCcAddresses] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [sending, setSending] = useState(false)
  const [previewing, setPreviewing] = useState(false)

  // Feature #170: merge field insertion panel state.
  const [mergeFields, setMergeFields] = useState<MergeFieldOption[]>([])
  const [mergeFieldsOpen, setMergeFieldsOpen] = useState(false)

  // Feature #170: ref for the rich text contentEditable div.
  const editorRef = useRef<HTMLDivElement>(null)

  // Expanded message state — show full body on click.
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getEmails(applicationId)
      // Newest first.
      setEmails([...result].sort((a, b) => {
        const aTs = new Date(a.sentAt || a.createdAt).getTime()
        const bTs = new Date(b.sentAt || b.createdAt).getTime()
        return bTs - aTs
      }))
    } catch {
      setError('Unable to load emails')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Feature #238: parent (ApplicationDetail) sets autoOpenCompose=true
  // after transitioning to Outcome=Unsuccessful so the agent can dash off
  // a regret email without an extra click. Acknowledge by calling
  // ``onAutoOpenHandled`` so the same signal can fire again on a future
  // transition.
  useEffect(() => {
    if (autoOpenCompose && !loading) {
      openCompose()
      onAutoOpenHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCompose, loading])

  const openCompose = async () => {
    setComposeOpen(true)
    setSelectedTemplateKey('')
    setFromAddress('')
    setToAddresses(defaultToAddress || '')
    setCcAddresses('')
    setSubject('')
    setBodyHtml('')
    setMergeFieldsOpen(false)
    // Clear the editor content on open.
    if (editorRef.current) editorRef.current.innerHTML = ''

    // Fetch from-addresses, merge fields, and templates in parallel.
    const promises: Promise<void>[] = []

    if (fromAddresses.length === 0) {
      promises.push(
        getFromAddresses(applicationId).then((list) => {
          setFromAddresses(list)
          if (list.length > 0) setFromAddress(list[0].address)
        }).catch(() => { /* non-fatal */ })
      )
    } else if (fromAddresses.length > 0) {
      setFromAddress(fromAddresses[0].address)
    }

    if (mergeFields.length === 0) {
      promises.push(
        getMergeFields(applicationId).then(setMergeFields).catch(() => {})
      )
    }

    if (templates.length === 0) {
      setTemplatesLoading(true)
      promises.push(
        getEmailTemplates().then(setTemplates).catch(() => {}).finally(() => setTemplatesLoading(false))
      )
    }

    await Promise.all(promises)
  }

  const applyTemplate = async (key: string) => {
    setSelectedTemplateKey(key)
    if (!key) return
    setPreviewing(true)
    try {
      const preview = await composeEmail(applicationId, { templateKey: key })
      setSubject(preview.subject)
      setBodyHtml(preview.bodyHtml)
      if (editorRef.current) editorRef.current.innerHTML = preview.bodyHtml
      if (!toAddresses && preview.toAddresses) setToAddresses(preview.toAddresses)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail || 'Failed to render template')
    } finally {
      setPreviewing(false)
    }
  }

  // Feature #170: sync the contentEditable div's innerHTML back to state.
  const syncEditorToState = useCallback(() => {
    if (editorRef.current) {
      setBodyHtml(editorRef.current.innerHTML)
    }
  }, [])

  // Feature #170: rich text formatting commands.
  const execFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    syncEditorToState()
  }

  // Feature #170: insert a merge field tag at the current cursor position.
  const insertMergeField = (key: string) => {
    const tag = `{{${key}}}`
    editorRef.current?.focus()
    document.execCommand('insertText', false, tag)
    syncEditorToState()
  }

  const handleSend = async () => {
    // Read the latest editor content before validation.
    const currentBody = editorRef.current?.innerHTML || bodyHtml
    if (!subject.trim() || !currentBody.trim() || !toAddresses.trim()) {
      alert('To, subject, and body are required.')
      return
    }
    setSending(true)
    try {
      await sendEmail(applicationId, {
        subject: subject.trim(),
        bodyHtml: currentBody,
        fromAddress: fromAddress || undefined,
        toAddresses: toAddresses.trim(),
        ccAddresses: ccAddresses.trim() || undefined,
        templateKey: selectedTemplateKey || undefined,
      })
      setComposeOpen(false)
      await load()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail || 'Failed to send email')
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Emails</h3>
        <button onClick={openCompose} style={primaryBtn}>Compose</button>
      </div>

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading emails…</div>}
      {error && <div style={{ color: 'var(--color-magenta)' }}>{error}</div>}

      {!loading && !error && emails.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>
            No emails sent or received for this application yet.
          </div>
        </div>
      )}

      {emails.map((m) => {
        const expanded = expandedId === m.id
        return (
          <div
            key={m.id}
            style={{ ...cardStyle, cursor: 'pointer' }}
            onClick={() => setExpandedId(expanded ? null : m.id)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={directionBadgeStyle(m.direction)}>{m.direction || 'Unknown'}</span>
                <strong style={{ fontSize: '14px' }}>{m.subject || '(no subject)'}</strong>
                {m.templateKey && (
                  <span style={{
                    padding: '2px 8px',
                    backgroundColor: 'var(--color-light-grey)',
                    borderRadius: '999px',
                    fontSize: '11px',
                    color: 'var(--color-dark-grey)',
                  }}>
                    {m.templateKey}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                {formatDateTime(m.sentAt || m.createdAt)}
              </div>
            </div>

            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '6px' }}>
              {m.direction?.toLowerCase() === 'outbound' ? 'Sent by ' : 'From '}
              <strong>{m.sentByDisplayName || m.fromAddress || m.sentByUserId || 'Unknown'}</strong>
              {m.toAddresses && (
                <>
                  {' '}· To <strong>{m.toAddresses}</strong>
                </>
              )}
              {m.ccAddresses && (
                <>
                  {' '}· Cc <strong>{m.ccAddresses}</strong>
                </>
              )}
            </div>

            {!expanded && m.bodyPreview && (
              <div style={{ fontSize: '13px', color: 'var(--color-black)' }}>
                {m.bodyPreview}
              </div>
            )}
            {expanded && m.bodyHtml && (
              <div
                style={{
                  fontSize: '13px',
                  color: 'var(--color-black)',
                  backgroundColor: 'var(--color-light-grey)',
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '6px',
                }}
                // Template-authored HTML is admin-curated, not user input, so
                // rendering it is acceptable for the preview panel.
                dangerouslySetInnerHTML={{ __html: m.bodyHtml }}
              />
            )}
          </div>
        )
      })}

      <Modal
        isOpen={composeOpen}
        title="Compose email"
        onClose={() => setComposeOpen(false)}
        size="lg"
        isDirty={subject !== '' || bodyHtml !== '' || toAddresses !== '' || fromAddress !== ''}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setComposeOpen(false)}
              style={secondaryBtn}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              style={{
                ...primaryBtn,
                opacity: sending ? 0.6 : 1,
                cursor: sending ? 'not-allowed' : 'pointer',
              }}
              disabled={sending}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Template</label>
            <select
              value={selectedTemplateKey}
              onChange={(e) => applyTemplate(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
              disabled={templatesLoading || previewing}
            >
              <option value="">— No template (free-form) —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.key}>
                  {t.name}
                </option>
              ))}
            </select>
            {templatesLoading && (
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '4px' }}>
                Loading templates…
              </div>
            )}
            {previewing && (
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '4px' }}>
                Rendering template…
              </div>
            )}
          </div>

          {/* Feature #226: From is locked to the shared careers mailbox so
              inbound replies route to the platform's single monitored inbox.
              Render as a read-only field with a clear label rather than a
              one-option dropdown to avoid implying user choice. If the
              backend ever returns more than one option (re-introducing
              shared mailboxes), fall back to a select. */}
          <div>
            <label style={labelStyle}>From</label>
            {fromAddresses.length > 1 ? (
              <select
                value={fromAddress}
                onChange={(e) => setFromAddress(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {fromAddresses.map((fa) => (
                  <option key={fa.address} value={fa.address}>
                    {fa.label}{fa.isShared ? ' (shared)' : ''} — {fa.address}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="email"
                value={fromAddresses[0]?.address || fromAddress}
                readOnly
                style={{ ...inputStyle, backgroundColor: 'var(--color-light-grey)', cursor: 'not-allowed' }}
              />
            )}
          </div>

          <div>
            <label style={labelStyle}>To</label>
            <input
              type="email"
              value={toAddresses}
              onChange={(e) => setToAddresses(e.target.value)}
              placeholder="recipient@example.com"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>CC (optional)</label>
            <input
              type="text"
              value={ccAddresses}
              onChange={(e) => setCcAddresses(e.target.value)}
              placeholder="cc@example.com"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Feature #170: Rich text toolbar + contentEditable body editor */}
          <div>
            <label style={labelStyle}>Body</label>
            <div style={{
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              overflow: 'hidden',
            }}>
              {/* Formatting toolbar */}
              <div style={{
                display: 'flex',
                gap: '2px',
                padding: '6px 8px',
                borderBottom: '1px solid var(--color-light-grey)',
                backgroundColor: '#f9fafb',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}>
                <button type="button" onClick={() => execFormat('bold')} title="Bold"
                  style={{ ...toolbarBtnStyle, fontWeight: 700 }}>B</button>
                <button type="button" onClick={() => execFormat('italic')} title="Italic"
                  style={{ ...toolbarBtnStyle, fontStyle: 'italic' }}>I</button>
                <button type="button" onClick={() => execFormat('underline')} title="Underline"
                  style={{ ...toolbarBtnStyle, textDecoration: 'underline' }}>U</button>
                <span style={{ width: '1px', height: '18px', backgroundColor: 'var(--color-light-grey)', margin: '0 4px' }} />
                <button type="button" onClick={() => execFormat('insertUnorderedList')} title="Bullet list"
                  style={toolbarBtnStyle}>• List</button>
                <button type="button" onClick={() => execFormat('insertOrderedList')} title="Numbered list"
                  style={toolbarBtnStyle}>1. List</button>
                <span style={{ width: '1px', height: '18px', backgroundColor: 'var(--color-light-grey)', margin: '0 4px' }} />
                <button type="button" onClick={() => {
                  const url = prompt('Enter URL:')
                  if (url) execFormat('createLink', url)
                }} title="Insert link" style={toolbarBtnStyle}>Link</button>
                <button type="button" onClick={() => execFormat('removeFormat')} title="Clear formatting"
                  style={toolbarBtnStyle}>Clear</button>
                <span style={{ width: '1px', height: '18px', backgroundColor: 'var(--color-light-grey)', margin: '0 4px' }} />
                <button
                  type="button"
                  onClick={() => setMergeFieldsOpen(!mergeFieldsOpen)}
                  title="Insert merge field"
                  style={{
                    ...toolbarBtnStyle,
                    backgroundColor: mergeFieldsOpen ? 'var(--color-magenta)' : undefined,
                    color: mergeFieldsOpen ? 'var(--color-white)' : undefined,
                  }}
                >{'{{'} Merge fields</button>
              </div>

              {/* Feature #170: Merge field insertion panel */}
              {mergeFieldsOpen && mergeFields.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  padding: '8px',
                  borderBottom: '1px solid var(--color-light-grey)',
                  backgroundColor: '#fdf2f8',
                  flexWrap: 'wrap',
                }}>
                  {mergeFields.map((mf) => (
                    <button
                      key={mf.key}
                      type="button"
                      onClick={() => insertMergeField(mf.key)}
                      title={mf.sampleValue ? `Sample: ${mf.sampleValue}` : mf.key}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontFamily: 'var(--font-family)',
                        backgroundColor: 'var(--color-white)',
                        border: '1px solid var(--color-light-grey)',
                        borderRadius: '999px',
                        cursor: 'pointer',
                        color: 'var(--color-black)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {mf.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Editable content area */}
              <div
                ref={editorRef}
                contentEditable
                onInput={syncEditorToState}
                onBlur={syncEditorToState}
                data-placeholder="Write your message…"
                style={{
                  minHeight: '200px',
                  padding: '12px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  outline: 'none',
                  lineHeight: '1.6',
                  overflowY: 'auto',
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default EmailsTab
